import { Divider, HStack, Text } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useCartContext } from 'context/CartContext/CartContext';
import { CalculatePriceResponse } from 'api/responseTypes';

type TicketGroup = { key: string; price: number; count: number };

type TicketPriceProps = {
  priceCalculation?: CalculatePriceResponse | null;
};

function groupByCategory(summary: CalculatePriceResponse['ticketsSummary']): TicketGroup[] {
  const map = new Map<string, TicketGroup>();
  for (const ts of summary) {
    const existing = map.get(ts.categoryKey);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(ts.categoryKey, { key: ts.categoryKey, price: ts.price, count: 1 });
    }
  }
  return Array.from(map.values());
}

function groupByPrice(tickets: { price: number }[]): TicketGroup[] {
  const map = new Map<number, TicketGroup>();
  for (const ticket of tickets) {
    const existing = map.get(ticket.price);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(ticket.price, { key: String(ticket.price), price: ticket.price, count: 1 });
    }
  }
  return Array.from(map.values());
}

function formatPrice(count: number, price: number): string {
  const formatted = new Intl.NumberFormat().format(price);
  return count > 1 ? `${count} x ${formatted} د.ع` : `${formatted} د.ع`;
}

export const TicketPrice = ({ priceCalculation }: TicketPriceProps) => {
  const { tickets, discountAmount } = useCartContext();

  const groups = priceCalculation?.ticketsSummary
    ? groupByCategory(priceCalculation.ticketsSummary)
    : groupByPrice(tickets);

  return (
    <>
      {groups.map((group) => (
        // Label first: under `dir='rtl'` the first child lands on the **right**, which is where the
        // design puts every row label, with the amount opposite it (`Ticket Zone Checkout Summary.png`).
        <HStack justify='space-between' key={group.key}>
          <Text variant='bodySmallRegular'>
            <FormattedMessage id='checkout.ticketPrice' defaultMessage={'Ticket price'} />
          </Text>
          <Text variant='bodySmallRegular'>{formatPrice(group.count, group.price)}</Text>
        </HStack>
      ))}

      {priceCalculation?.subTotal != null && priceCalculation.subTotal > 0 && (
        <>
          {/* Hairline between the bill lines, per `Ticket Zone Checkout Summary.png`. */}
          <Divider borderColor='dark.D100' />
          <HStack justify='space-between'>
            <Text variant='bodySmallRegular'>
              <FormattedMessage id='checkout.subtotal' defaultMessage='Subtotal' />
            </Text>
            <Text variant='bodySmallRegular'>
              {new Intl.NumberFormat().format(priceCalculation.subTotal)} د.ع
            </Text>
          </HStack>
        </>
      )}

      {!priceCalculation?.ticketsSummary && discountAmount != null && discountAmount > 0 && (
        <HStack justify='space-between' mt='2px'>
          <Text variant='bodySmallRegular' color='green.G600'>
            <FormattedMessage id='checkout.discount' defaultMessage='Discount' />
          </Text>
          <Text variant='bodySmallRegular' color='green.G600'>
            -{new Intl.NumberFormat().format(discountAmount)} د.ع
          </Text>
        </HStack>
      )}
    </>
  );
};
